import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@app/feature/authentication/store/auth.store';
import { AuthenticationService } from '@app/feature/authentication/service/authentication.service';
import { safeGet, safeJsonParse } from '@core/utils/safe-storage.util';
import { catchError, map, of } from 'rxjs';

export const AdminGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const authService = inject(AuthenticationService);

  const getRole = (respUser?: unknown): string | null => {
    // Le claim JWT (signé serveur) prime sur le store et le stockage local
    // (réinscriptible depuis le navigateur) pour la répartition des espaces.
    const claim = authService.getRole();
    if (claim) return claim;
    const user = authStore.user();
    const raw = safeGet('user');
    const storedUser = safeJsonParse<{ role?: string | null } | null>(
      raw,
      null,
    );
    const fromResp = (respUser as { role?: string | null } | null)?.role;
    const userData = (respUser as object) ?? user ?? storedUser;
    return (
      fromResp ?? (userData as { role?: string | null } | null)?.role ?? null
    );
  };

  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    // Rendu serveur : pas de cookie, pas de refresh ; décision synchrone sûre.
    if (!authService.isTokenValid()) {
      return router.createUrlTree(['/authentication/login']);
    }
    try {
      return getRole() === 'ADMIN'
        ? true
        : router.createUrlTree(['/error/forbidden']);
    } catch {
      return router.createUrlTree(['/authentication/login']);
    }
  }

  if (authService.isTokenValid()) {
    try {
      const role = getRole();
      if (role === 'ADMIN') {
        return true;
      }
      router.navigate(['/error/forbidden']);
      return false;
    } catch {
      router.navigate(['/authentication/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }
  }

  if (!authService.hasSessionHint()) {
    router.navigate(['/authentication/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  return authService.refreshToken().pipe(
    map((response) => {
      if (!authService.isTokenValid(response.accessToken) || !response.user) {
        // Réponse de refresh incomplète : ne jamais réutiliser un rôle périmé.
        authService.clearLocalSession();
        router.navigate(['/authentication/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }
      const role = getRole(response.user);
      if (role === 'ADMIN') {
        return true;
      }
      router.navigate(['/error/forbidden']);
      return false;
    }),
    catchError(() => {
      authService.clearLocalSession();
      router.navigate(['/authentication/login'], {
        queryParams: { returnUrl: state.url },
      });
      return of(false);
    }),
  );
};
